import {
  EventHandler,
  Fragment,
  KeyboardEventHandler,
  MouseEventHandler,
  memo,
  useContext,
  MouseEvent,
  KeyboardEvent,
  CSSProperties,
} from 'react'
import { AllStatuses } from '@opallabs/concepts'
import { PartialPost } from '@opallabs/continuum/src/WeekView/momentTypes'
import { WorkflowIcon } from '@opallabs/lapidary'
import { AllowedEvents, analyticsContext } from '@opallabs/quanta'
import Conditional from '../Conditional'
import { kuiperClassNames, renderIcon } from '../utils'
import {
  ApprovalStatuses,
  ContentApprovalStatus,
  ariaLabels,
  generateStatusString,
  getDeliveryStatus,
  postPublishingMode,
} from './helpers'
import './styles.scss'

const isApproval = (test: string = '') => !!test.match(/approval/)

type ApprovalPhaseIconProps = Pick<
  StatusIconProps,
  | 'approvalStatuses'
  | 'content'
  | 'hideApprovalDot'
  | 'hideDeliveryRing'
  | 'isInPopover'
  | 'tabIndex'
  | 'workflow'
  | 'workflowStarted'
>

const statusToIcon: Record<string, AllStatuses> = {
  'is-draft': 'approval-not-sent',
  'is-pending': 'approval-pending',
  'is-denied': 'approval-denied',
  'is-approved': 'approval-approved',
  'is-published': 'published',
}

function ApprovalPhaseIcon({
  approvalStatuses,
  content,
  hideApprovalDot,
  hideDeliveryRing,
  isInPopover,
  tabIndex,
  workflow,
  workflowStarted,
}: ApprovalPhaseIconProps) {
  const approvalStatusString = generateStatusString({
    entity: content,
    workflow,
    approvalStatuses,
  })
  const approvalStatusClass = `kuiper-status approvalStatus-dot sprite-approval_status ${approvalStatusString}`
  const deliveryStatus = getDeliveryStatus(content)
  const deliveryStatusClass = postPublishingMode(content, deliveryStatus)

  const styleOverrides: CSSProperties = {}
  if (isInPopover) {
    styleOverrides.cursor = 'pointer'
    if (workflowStarted === false) {
      styleOverrides.opacity = 0.3 // reduced opacity to indicate Workflow has not started yet
    }
  }

  const iconStatus =
    statusToIcon[approvalStatusString.trim()] || 'approval-not-sent'

  if (hideDeliveryRing) {
    return <WorkflowIcon status={iconStatus} />
  }

  return (
    <Fragment>
      <Conditional condition={!hideApprovalDot}>
        <div
          aria-label={
            ariaLabels({ entity: content, workflow, approvalStatuses })
              .approvalStatus
          }
          role="button"
          className={approvalStatusClass}
          style={styleOverrides}
          tabIndex={tabIndex}
        />
      </Conditional>
      <Conditional condition={!hideDeliveryRing}>
        <div
          aria-label={
            ariaLabels({ entity: content, workflow, approvalStatuses })
              .publishingStatus
          }
          role="button"
          className={deliveryStatusClass}
          tabIndex={tabIndex}
        />
      </Conditional>
    </Fragment>
  )
}

function renderTaskPhaseIcon() {
  const options = {
    className: 'kuiper-approvalStatusCheckmark',
    height: 10,
    width: 10,
    ariaMode: 'hidden' as const,
  }
  const style = { color: '#A0AEB9', fill: '#A0AEB9' }
  return renderIcon('checkmark', { ...options, style })
}

type StatusIconProps = Pick<
  ApprovalStatusProps,
  | 'approvalStatuses'
  | 'content'
  | 'currentPhaseType'
  | 'finalApprovalReached'
  | 'hideApprovalDot'
  | 'hideDeliveryRing'
  | 'isInPopover'
  | 'tabIndex'
  | 'workflow'
  | 'workflowStarted'
> &
  Required<Pick<ApprovalStatusProps, 'approvalStatuses'>>

function StatusIcon({
  approvalStatuses,
  content,
  currentPhaseType,
  finalApprovalReached,
  hideApprovalDot,
  hideDeliveryRing,
  isInPopover,
  tabIndex,
  workflow,
  workflowStarted,
}: StatusIconProps) {
  const shouldRenderApprovalDot =
    finalApprovalReached ||
    !currentPhaseType ||
    !workflowStarted ||
    isApproval(currentPhaseType)

  if (shouldRenderApprovalDot) {
    // block handles cases when approval status icons render from content without workflow
    let statuses = approvalStatuses
    if (content && !approvalStatuses.length) {
      statuses = [content.status]
      if (content.is_published) {
        statuses.push('published')
      }
    }

    return (
      <ApprovalPhaseIcon
        {...{
          approvalStatuses: statuses,
          content,
          hideApprovalDot,
          hideDeliveryRing,
          isInPopover,
          tabIndex,
          workflow,
          workflowStarted,
        }}
      />
    )
  }
  return renderTaskPhaseIcon()
}

type ApprovalStatusProps = {
  approvalStatuses?: ApprovalStatuses
  content?: PartialPost
  currentPhaseType?: string
  finalApprovalReached?: boolean
  hideApprovalDot?: boolean
  hideDeliveryRing?: boolean
  isInPopover?: boolean
  onClick?: EventHandler<MouseEvent | KeyboardEvent>
  tabIndex?: number
  workflow?: { content_approval_status: ContentApprovalStatus }
  workflowStarted?: boolean
}

const ApprovalStatus = ({
  approvalStatuses = [],
  content,
  currentPhaseType,
  finalApprovalReached,
  hideApprovalDot = false,
  hideDeliveryRing = false,
  isInPopover = false,
  onClick,
  tabIndex = 0,
  workflow,
  workflowStarted,
}: ApprovalStatusProps) => {
  const { track } = useContext(analyticsContext)
  const handleClick: MouseEventHandler = (e) => {
    if (track) {
      track(AllowedEvents.click)
    }
    onClick?.(e)
  }
  const handleKeyUp: KeyboardEventHandler = (event) => {
    if ([' ', 'Enter'].includes(event.key)) {
      event.preventDefault()
      event.stopPropagation()

      if (track) {
        track(AllowedEvents.click)
      }
      onClick?.(event)
    }
  }

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions
    <div
      onClick={handleClick}
      className={kuiperClassNames('approvalStatus')}
      onKeyUp={handleKeyUp}
    >
      <StatusIcon
        {...{
          approvalStatuses,
          content,
          currentPhaseType,
          finalApprovalReached,
          hideApprovalDot,
          hideDeliveryRing,
          isInPopover,
          tabIndex,
          workflow,
          workflowStarted,
        }}
      />
    </div>
  )
}

export default memo(ApprovalStatus)
